/**
 * HOF-696 D20 — the office Timesheet at /apps/labour (seats with `labour.read`).
 *
 * People × 7 days: the roster (`GET /farm/labour-access`, D16) × one generic
 * aggregate of the week (D11/D14), with the confirmed tick from the week's
 * `work_day_report` rows. A past day with no minutes and no report is "fehlt" —
 * from the day the worker's access began (`access_since`), weekends included;
 * a pre-approved person who never signed in is "noch nicht angemeldet". Above
 * the grid, the access problems the office can fix, each linking to the person's
 * card; "Jetzt abgleichen" (D7) for `members.manage`. A cell opens that person's
 * entries for the day with the reflection (A4: the whole office sees it) —
 * read-only, unless the seat holds `labour.write` (HOF-697: the Vorarbeiter, the
 * owner, the farm manager): then a day inside the 8 editable days can be entered,
 * corrected and cleared for that person, and a picker adds an employed person
 * outside the grid (one without the app). The seat's own row stays read-only here
 * — its own time goes through "Heute". Without `farm.read` (a Vorarbeiter) there
 * are no person-card links and no access problems: neither is theirs to fix.
 * No CSV.
 *
 * Office copy is German, as the other app pages are; the worker screens are the
 * i18n'd ones (D19).
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../../../contexts/AuthContext';
import {
  catalogueLabel,
  classifyLabourError,
  errorCode,
  useLabourAccessSync,
  useLabourCatalogue,
  useLabourOfficeCommands,
  useLabourRoster,
  useOfficeCellEntries,
  useOfficeWeek,
  type LabourEntry,
  type RosterPerson,
} from '../../../hooks/useLabour';
import { hasSeatPermission } from '../../../utils/landing';
import { addDays, berlinDate, formatDayLabel, isEditableDate, isoWeekDates } from './labourDates';
import { DEFAULT_DURATION, DURATION_MAX, DURATION_MIN, DURATION_STEP, newClientId } from './labourDraft';
import { GRID_STATUSES, accessProblem, formatMinutes } from './labourFormat';
import './Labour.css';

const defaultNow = () => new Date();

export interface TimesheetPageProps {
  now?: () => Date;
}

const personCardHref = (personId: string) => `/farm?lense=persons&item=${encodeURIComponent(personId)}`;

const DURATIONS = Array.from(
  { length: (DURATION_MAX - DURATION_MIN) / DURATION_STEP + 1 },
  (_, i) => DURATION_MIN + i * DURATION_STEP,
);

/** The open entry form of the day panel (HOF-697). */
interface EntryForm {
  clientId: string;
  activityKey: string;
  costObjectKey: string;
  durationMin: number;
  note: string;
}

/** An on-behalf write refused, in the office's words. */
function writeErrorText(error: unknown): string {
  const kind = classifyLabourError(error);
  const code = errorCode(error);
  if (kind === 'not_employed') return 'Diese Person ist auf ihrer Karte nicht als beschäftigt eingetragen.';
  if (code === 'person_not_found') return 'Diese Person gibt es nicht mehr.';
  if (code === 'own_entries_via_me') return 'Deine eigene Zeit trägst du unter „Heute“ ein.';
  if (kind === 'day_cap') return 'Mehr als 16 Stunden an einem Tag geht nicht.';
  if (kind === 'gone') return 'Dieser Eintrag wurde schon gelöscht.';
  if (kind === 'busy' || kind === 'unavailable' || kind === 'network')
    return 'Gerade beschäftigt oder keine Verbindung. Bitte gleich nochmal versuchen.';
  if (kind === 'app_not_active') return 'Die Arbeit-App ist für diesen Betrieb nicht eingeschaltet.';
  if (kind === 'forbidden' || kind === 'access_ended') return 'Dein Zugang erlaubt diese Änderung nicht.';
  if (kind === 'invalid') return 'Das ging nicht. Bitte prüfe den Eintrag.';
  return 'Das hat nicht geklappt. Bitte nochmal versuchen.';
}

const TimesheetPage: React.FC<TimesheetPageProps> = ({ now = defaultNow }) => {
  const { userProfile } = useAuth();
  const farm = userProfile?.active_farm;
  const canSync = hasSeatPermission(farm, 'members.manage');
  const canWrite = hasSeatPermission(farm, 'labour.write');
  const canOpenCards = hasSeatPermission(farm, 'farm.read');
  const ownPersonId = farm?.person_id ?? null;

  const today = berlinDate(now());
  const [weekOf, setWeekOfState] = useState(today);
  const week = useMemo(() => isoWeekDates(weekOf), [weekOf]);
  const isCurrentWeek = week.includes(today);
  const [selected, setSelectedState] = useState<{ personId: string; day: string } | null>(null);
  const [form, setForm] = useState<EntryForm | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  // People added to the grid by the picker (HOF-697): employed, outside the grid, given time here.
  const [addedIds, setAddedIds] = useState<string[]>([]);
  // Another cell closes the open form; another week closes the day panel: its status and
  // reflection come from the shown week's reports.
  const setSelected = (next: { personId: string; day: string } | null) => {
    setSelectedState(next);
    setForm(null);
    setWriteError(null);
  };
  const setWeekOf = (day: string) => {
    setSelected(null);
    setWeekOfState(day);
  };

  const roster = useLabourRoster();
  const weekData = useOfficeWeek(week);
  const catalogue = useLabourCatalogue();
  const sync = useLabourAccessSync();
  const cell = useOfficeCellEntries(selected?.personId ?? null, selected?.day ?? null);
  const { putPersonEntry, deletePersonEntry } = useLabourOfficeCommands();
  /** May this seat enter or correct this person's day here? */
  const writable = (personId: string, day: string) =>
    canWrite && personId !== ownPersonId && isEditableDate(day, today);

  const people = useMemo(() => roster.data ?? [], [roster.data]);
  const rows = useMemo(() => {
    const byId = new Map(people.map((p) => [p.personId, p]));
    const inGrid: RosterPerson[] = people.filter((p) => GRID_STATUSES.has(p.status) || addedIds.includes(p.personId));
    // Anyone with minutes this week stays visible even if their card changed since.
    for (const personId of Object.keys(weekData.data?.minutes ?? {})) {
      if (!inGrid.some((p) => p.personId === personId)) {
        inGrid.push(byId.get(personId) ?? { personId, displayName: personId, status: 'unknown' });
      }
    }
    return inGrid.sort((a, b) => a.displayName.localeCompare(b.displayName, 'de'));
  }, [people, weekData.data, addedIds]);
  // Employed people outside the grid the office may give time to (the roster's not_employed excluded).
  const addable = useMemo(
    () =>
      canWrite
        ? people
            .filter((p) => p.status !== 'not_employed' && p.personId !== ownPersonId && !rows.some((r) => r.personId === p.personId))
            .sort((a, b) => a.displayName.localeCompare(b.displayName, 'de'))
        : [],
    [canWrite, people, rows, ownPersonId],
  );
  const problems = useMemo(
    () =>
      people
        .map((p) => ({ person: p, label: accessProblem(p) }))
        .filter((p): p is { person: RosterPerson; label: string } => p.label !== null)
        .sort((a, b) => a.person.displayName.localeCompare(b.person.displayName, 'de')),
    [people],
  );

  const reportFor = (personId: string, day: string) =>
    weekData.data?.reports.find((r) => r.personId === personId && r.workDay === day);
  const minutesFor = (personId: string, day: string) => weekData.data?.minutes[personId]?.[day] ?? 0;
  const weekMinutesFor = (personId: string) => week.reduce((sum, d) => sum + minutesFor(personId, d), 0);

  const activities = catalogue.data?.activities ?? [];
  const costObjects = catalogue.data?.costObjects ?? [];

  const readError = roster.error ?? weekData.error;
  if (readError) {
    const kind = classifyLabourError(readError);
    return (
      <div className="lab-office">
        <h1>Arbeit</h1>
        <p className="mgl-card">
          {kind === 'app_not_active'
            ? 'Die Arbeit-App ist für diesen Betrieb nicht eingeschaltet.'
            : kind === 'forbidden'
              ? 'Dein Zugang erlaubt die Wochenübersicht nicht.'
              : 'Die Wochenübersicht konnte nicht geladen werden.'}
        </p>
      </div>
    );
  }

  const range = `${formatDayLabel(week[0], 'de', { day: 'numeric', month: 'short' })} – ${formatDayLabel(week[6], 'de', {
    day: 'numeric',
    month: 'short',
  })}`;

  const renderCell = (person: RosterPerson, day: string) => {
    if (day > today) return <span className="lab-muted">·</span>;
    const isSelected = selected?.personId === person.personId && selected.day === day;
    const open = () => setSelected(isSelected ? null : { personId: person.personId, day });
    const cls = `lab-cell${isSelected ? ' lab-cell--selected' : ''}`;
    // HOF-697: an empty day the seat may write opens the panel to enter time.
    const enter = (
      <button type="button" className={cls} aria-pressed={isSelected} aria-label="Zeit erfassen" onClick={open}>
        +
      </button>
    );
    const canEnter = writable(person.personId, day);
    if (person.status === 'pending') return canEnter ? enter : <span className="lab-muted">–</span>;
    const minutes = minutesFor(person.personId, day);
    const report = reportFor(person.personId, day);
    if (minutes > 0 || report?.confirmed) {
      return (
        <button type="button" className={cls} aria-pressed={isSelected} onClick={open}>
          <span className="mgl-num">{formatMinutes(minutes)}</span>
          {report?.confirmed && (
            <span className="lab-tick" aria-label="bestätigt">
              {' '}
              ✓
            </span>
          )}
        </button>
      );
    }
    // Before the worker's access began there was nothing to report: empty, not "fehlt" (ui e2e).
    if (person.accessSince && day < person.accessSince) {
      return canEnter ? enter : <span className="lab-muted" title="Noch kein Zugang an diesem Tag" />;
    }
    if (day < today && person.status === 'signed_in') {
      return (
        <button type="button" className={`${cls} lab-cell--missing`} aria-pressed={isSelected} onClick={open}>
          fehlt
        </button>
      );
    }
    return canEnter ? enter : <span className="lab-muted">–</span>;
  };

  const selectedPerson = selected ? rows.find((p) => p.personId === selected.personId) : undefined;
  const selectedReport = selected ? reportFor(selected.personId, selected.day) : undefined;
  const selectedWritable = selected ? writable(selected.personId, selected.day) : false;
  const activityName = (key: string) => catalogueLabel(activities.find((a) => a.key === key), 'de') || key;
  const costName = (key: string) => catalogueLabel(costObjects.find((c) => c.key === key), 'de') || key;

  const startEntry = () =>
    setForm({ clientId: newClientId(), activityKey: '', costObjectKey: '', durationMin: DEFAULT_DURATION, note: '' });
  const startEdit = (entry: LabourEntry) => {
    if (!entry.clientId) return;
    setWriteError(null);
    setForm({
      clientId: entry.clientId,
      activityKey: entry.activityKey,
      costObjectKey: entry.costObjectKey,
      durationMin: entry.durationMin,
      note: entry.note,
    });
  };
  const saveEntry = () => {
    if (!selected || !form?.activityKey || !form.costObjectKey) return;
    setWriteError(null);
    putPersonEntry.mutate(
      {
        personId: selected.personId,
        clientId: form.clientId,
        input: {
          workDay: selected.day,
          activityKey: form.activityKey,
          costObjectKey: form.costObjectKey,
          durationMin: form.durationMin,
          note: form.note,
        },
      },
      { onSuccess: () => setForm(null), onError: (error) => setWriteError(writeErrorText(error)) },
    );
  };
  const removeEntry = (entry: LabourEntry) => {
    if (!selected || !entry.clientId) return;
    setWriteError(null);
    deletePersonEntry.mutate(
      { personId: selected.personId, clientId: entry.clientId },
      { onError: (error) => setWriteError(writeErrorText(error)) },
    );
  };

  const renderForm = (current: EntryForm) => {
    const crops = costObjects.filter((c) => c.kind === 'crop');
    const areas = costObjects.filter((c) => c.kind === 'area');
    const set = (patch: Partial<EntryForm>) => setForm({ ...current, ...patch });
    return (
      <div className="lab-office-form">
        <label className="lab-field">
          <span className="lab-field__label">Tätigkeit</span>
          <select className="lab-input" value={current.activityKey} onChange={(e) => set({ activityKey: e.target.value })}>
            <option value="">Bitte wählen …</option>
            {activities.map((a) => (
              <option key={a.key} value={a.key}>
                {catalogueLabel(a, 'de')}
              </option>
            ))}
          </select>
        </label>
        <label className="lab-field">
          <span className="lab-field__label">Wo</span>
          <select className="lab-input" value={current.costObjectKey} onChange={(e) => set({ costObjectKey: e.target.value })}>
            <option value="">Bitte wählen …</option>
            {crops.length > 0 && (
              <optgroup label="Kultur">
                {crops.map((c) => (
                  <option key={c.key} value={c.key}>
                    {catalogueLabel(c, 'de')}
                  </option>
                ))}
              </optgroup>
            )}
            {areas.length > 0 && (
              <optgroup label="Bereich">
                {areas.map((c) => (
                  <option key={c.key} value={c.key}>
                    {catalogueLabel(c, 'de')}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </label>
        <label className="lab-field">
          <span className="lab-field__label">Dauer</span>
          <select
            className="lab-input"
            value={String(current.durationMin)}
            onChange={(e) => set({ durationMin: Number(e.target.value) })}
          >
            {/* A stored duration off the 15-min grid stays selectable as it is. */}
            {!DURATIONS.includes(current.durationMin) && (
              <option value={String(current.durationMin)}>{formatMinutes(current.durationMin)} h</option>
            )}
            {DURATIONS.map((m) => (
              <option key={m} value={String(m)}>
                {formatMinutes(m)} h
              </option>
            ))}
          </select>
        </label>
        <label className="lab-field">
          <span className="lab-field__label">Notiz</span>
          <input
            className="lab-input"
            maxLength={500}
            value={current.note}
            onChange={(e) => set({ note: e.target.value })}
          />
        </label>
        <div className="lab-office__head">
          <button
            type="button"
            className="mgl-btn mgl-btn--primary"
            disabled={!current.activityKey || !current.costObjectKey || putPersonEntry.isPending}
            onClick={saveEntry}
          >
            {putPersonEntry.isPending ? 'Speichert …' : 'Speichern'}
          </button>
          <button type="button" className="mgl-btn mgl-btn--ghost" onClick={() => setForm(null)}>
            Abbrechen
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="lab-office">
      <div className="lab-office__head">
        <h1>Arbeit · {range}</h1>
        <button type="button" className="mgl-btn" aria-label="Vorherige Woche" onClick={() => setWeekOf(addDays(week[0], -7))}>
          ‹
        </button>
        <button
          type="button"
          className="mgl-btn"
          aria-label="Nächste Woche"
          disabled={isCurrentWeek}
          onClick={() => setWeekOf(addDays(week[0], 7))}
        >
          ›
        </button>
        {!isCurrentWeek && (
          <button type="button" className="mgl-btn mgl-btn--ghost" onClick={() => setWeekOf(today)}>
            Diese Woche
          </button>
        )}
        <span className="lab-office__spacer" />
        {canSync && (
          <button type="button" className="mgl-btn mgl-btn--primary" disabled={sync.isPending} onClick={() => sync.mutate()}>
            Jetzt abgleichen
          </button>
        )}
      </div>
      {sync.isSuccess && <p className="lab-notice" role="status">Abgeglichen. Zugänge sind auf dem neuesten Stand.</p>}
      {sync.isError && <p className="lab-notice" role="status">Der Abgleich hat nicht geklappt. Bitte später nochmal.</p>}

      {canOpenCards && problems.length > 0 && (
        <section className="lab-issues" aria-labelledby="lab-issues-title">
          <b id="lab-issues-title">
            {problems.length === 1 ? '1 Person ohne Zugang' : `${problems.length} Personen ohne Zugang`} — auf der Karte
            beheben
          </b>
          <ul>
            {problems.map(({ person: p, label }) => (
              <li key={p.personId}>
                <Link to={personCardHref(p.personId)}>{p.displayName}</Link> · {label}
              </li>
            ))}
          </ul>
        </section>
      )}

      {weekData.data?.truncated && (
        <p className="lab-notice" role="status">
          Die Woche ist nicht vollständig geladen — zu viele Einträge auf einmal.
        </p>
      )}

      <div className="mgl-card lab-grid-wrap">
        {roster.isLoading || weekData.isLoading ? (
          <p className="lab-muted">Lädt …</p>
        ) : rows.length === 0 ? (
          <p className="lab-muted">Noch niemand hat Zugang zur Arbeit-App.</p>
        ) : (
          <div className="lab-grid-scroll">
            <table className="lab-grid">
              <thead>
                <tr>
                  <th scope="col" className="lab-grid__person">
                    Person
                  </th>
                  {week.map((d) => (
                    <th key={d} scope="col">
                      {formatDayLabel(d, 'de', { weekday: 'short', day: 'numeric' })}
                    </th>
                  ))}
                  <th scope="col" className="lab-grid__total">
                    Woche
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((person) => (
                  <tr key={person.personId}>
                    <td className="lab-grid__person">
                      {person.displayName}
                      {person.status === 'pending' && <span className="lab-pending"> noch nicht angemeldet</span>}
                      {person.status === 'ended' && <span className="lab-pending"> Zugang beendet</span>}
                    </td>
                    {week.map((d) => (
                      <td key={d}>{renderCell(person, d)}</td>
                    ))}
                    <td className="lab-grid__total">
                      {weekMinutesFor(person.personId) > 0 ? (
                        <b className="mgl-num">{formatMinutes(weekMinutesFor(person.personId))}</b>
                      ) : (
                        <span className="lab-muted">–</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {addable.length > 0 && (
        <label className="lab-field lab-office-add">
          <span className="lab-field__label">Zeit für weitere Person erfassen</span>
          <select
            className="lab-input"
            value=""
            onChange={(e) => {
              const id = e.target.value;
              if (id) setAddedIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
            }}
          >
            <option value="">Person wählen …</option>
            {addable.map((p) => (
              <option key={p.personId} value={p.personId}>
                {p.displayName}
              </option>
            ))}
          </select>
        </label>
      )}

      {selected && (
        <section
          className="mgl-card lab-detail"
          aria-label={`${selectedPerson?.displayName ?? selected.personId} · ${formatDayLabel(selected.day, 'de', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          })}`}
        >
          <div className="lab-office__head">
            <b>
              {selectedPerson?.displayName ?? selected.personId} ·{' '}
              {formatDayLabel(selected.day, 'de', { weekday: 'long', day: 'numeric', month: 'long' })}
            </b>
            <span className="lab-office__spacer" />
            <button type="button" className="mgl-btn mgl-btn--ghost" onClick={() => setSelected(null)}>
              Schließen
            </button>
          </div>
          <p className="lab-muted" role="status">
            {selectedReport?.confirmed ? 'Tag bestätigt' : 'Tag noch nicht bestätigt'}
          </p>
          {cell.isLoading ? (
            <p className="lab-muted">Lädt …</p>
          ) : (cell.data ?? []).length === 0 ? (
            <p className="lab-muted">Keine Einträge.</p>
          ) : (
            <ul>
              {(cell.data ?? []).map((entry, i) => (
                <li key={entry.entityId || i}>
                  {activityName(entry.activityKey)} · {costName(entry.costObjectKey)} · {formatMinutes(entry.durationMin)} h
                  {entry.note ? ` · ${entry.note}` : ''}
                  {selectedWritable && entry.clientId && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="mgl-btn mgl-btn--ghost"
                        aria-label={`Bearbeiten: ${activityName(entry.activityKey)}`}
                        onClick={() => startEdit(entry)}
                      >
                        Bearbeiten
                      </button>
                      <button
                        type="button"
                        className="mgl-btn mgl-btn--ghost"
                        aria-label={`Löschen: ${activityName(entry.activityKey)}`}
                        disabled={deletePersonEntry.isPending}
                        onClick={() => removeEntry(entry)}
                      >
                        Löschen
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          {selectedWritable &&
            (form ? (
              renderForm(form)
            ) : (
              <button type="button" className="mgl-btn" onClick={startEntry}>
                + Eintrag
              </button>
            ))}
          {writeError && (
            <p className="lab-notice" role="alert">
              {writeError}
            </p>
          )}
          {selectedReport?.reflection && (
            <p className="lab-reflection">
              <b>Rückmeldung:</b> {selectedReport.reflection}
            </p>
          )}
        </section>
      )}
    </div>
  );
};

export default TimesheetPage;
